import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Collapse,
  Chip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { WalteroCard, WalteroTypography, WalteroButton } from './waltero';
import PerformanceTab from './PerformanceTab';
import organizationRoleService from '../services/organizationRoleService';
import { hasAdminAccess } from '../utils/auth';
import { PerformanceDataProvider } from '../context/PerformanceDataContext';
import { SharedPerformanceCacheProvider, useSharedPerformanceCache } from '../context/SharedPerformanceCache';
import { API_BASE_URL } from '../config/api';
import axios from 'axios';

// Component that loads performance data for a single organization
const OrganizationPerformanceCard = ({ organization, onExpand }) => {
  const { loadAllPerformanceData, getSummaryStats } = useSharedPerformanceCache();
  const [performanceData, setPerformanceData] = useState({
    loading: true,
    data: null,
    error: null
  });
  const [expanded, setExpanded] = useState(false);
  const loadingRef = useRef(false);

  // Calculate date range for 1 week
  const getOneWeekDateRange = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 7);
    
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  // Load performance data when component mounts
  useEffect(() => {
    const loadData = async () => {
      // Prevent duplicate calls
      if (loadingRef.current) {
        return;
      }

      try {
        loadingRef.current = true;
        setPerformanceData(prev => ({ ...prev, loading: true, error: null }));
        
        const { startDate, endDate } = getOneWeekDateRange();
        await loadAllPerformanceData(organization.id, startDate, endDate);
        const summaryStats = getSummaryStats();
        
        console.log(`📊 Summary stats for org ${organization.id}:`, summaryStats);

        const summaryData = {
          connectionPercentage: summaryStats.averageConnectionPercentage || 0,
          batteryVoltage: summaryStats.averageBatteryVoltage || 0, // Get actual voltage in V
          successRate: summaryStats.averageSuccessRate || 0,
          averageRSSI: summaryStats.averageRSSI || 0,
          deviceCount: summaryStats.totalDevices || 0
        };

        setPerformanceData({
          loading: false,
          data: summaryData,
          error: null
        });

      } catch (err) {
        console.error('Error loading performance data:', err);
        setPerformanceData({
          loading: false,
          data: null,
          error: 'Failed to load performance data'
        });
      } finally {
        loadingRef.current = false;
      }
    };

    loadData();
  }, [organization.id, loadAllPerformanceData, getSummaryStats]);

  const handleExpand = () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    if (onExpand) {
      onExpand(organization.id, newExpanded);
    }
  };

  const renderSpeedometer = (value, label, color = 'primary', unit = '%', isLoading = false) => (
    <Box sx={{ textAlign: 'center', p: 1 }}>
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: isLoading 
            ? '#f0f0f0' 
            : `conic-gradient(${color} 0deg ${value * 3.6}deg, #e0e0e0 ${value * 3.6}deg 360deg)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 1
        }}
      >
        {isLoading ? (
          <CircularProgress size={24} />
        ) : (
          <Typography variant="h6" fontWeight="bold">
            {unit === 'V' ? value.toFixed(2) : Math.round(value)}{unit}
          </Typography>
        )}
      </Box>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
    </Box>
  );

  const roles = organization.roles || [];

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BusinessIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Box>
              <Typography variant="h6">{organization.name}</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                {roles.map(role => (
                  <Chip
                    key={role}
                    label={role === 'account-manager' ? 'Account Manager' : 'Support Responsible'}
                    size="small"
                    color={role === 'account-manager' ? 'primary' : 'secondary'}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {performanceData.loading && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {renderSpeedometer(0, 'Connection', 'primary', '%', true)}
                {renderSpeedometer(0, 'Success Rate', 'primary', '%', true)}
                {renderSpeedometer(0, 'RSSI', 'primary', 'dBm', true)}
                {renderSpeedometer(0, 'Battery', 'primary', 'V', true)}
                <Box sx={{ textAlign: 'center', p: 1 }}>
                  <CircularProgress size={24} />
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                    Devices
                  </Typography>
                </Box>
              </Box>
            )}
            
            {performanceData.error && !performanceData.loading && (
              <Typography variant="body2" color="error">
                Failed to load data
              </Typography>
            )}
            
            {performanceData.data && !performanceData.loading && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {renderSpeedometer(performanceData.data.connectionPercentage, 'Connection')}
                {renderSpeedometer(performanceData.data.successRate, 'Success Rate')}
                {renderSpeedometer(performanceData.data.averageRSSI, 'RSSI', 'primary', 'dBm')}
                {renderSpeedometer(performanceData.data.batteryVoltage, 'Battery', 'primary', 'V')}
                <Box sx={{ textAlign: 'center', p: 1 }}>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    {performanceData.data.deviceCount}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Devices
                  </Typography>
                </Box>
              </Box>
            )}
            
            {!performanceData.data && !performanceData.loading && (
              <Typography variant="body2" color="textSecondary">
                Performance data will load automatically...
              </Typography>
            )}
            
            <Button
              variant="outlined"
              endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={handleExpand}
              disabled={performanceData.loading}
            >
              {expanded ? 'Collapse' : 'View Details'}
            </Button>
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ mt: 2 }}>
            {performanceData.loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress />
              </Box>
            )}
            
            {performanceData.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {performanceData.error}
              </Alert>
            )}
            
            {performanceData.data && !performanceData.loading && (
              <PerformanceTab 
                selectedOrgId={organization.id}
                startDate={getOneWeekDateRange().startDate}
                endDate={getOneWeekDateRange().endDate}
              />
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

const PerformanceDashboard = () => {
  const isAdmin = hasAdminAccess();
  const [userOrganizations, setUserOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [expandedOrg, setExpandedOrg] = useState(null);

  // Get current user ID from localStorage
  const getCurrentUserId = () => {
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.userId || payload.sub;
      }
    } catch (error) {
      console.error('Error parsing token:', error);
    }
    return null;
  };


  // Load user's organization roles
  const loadUserOrganizations = useCallback(async () => {
    if (!isAdmin) {
      setUserOrganizations([]);
      setHasLoaded(true);
      setLoading(false);
      setError(null);
      return;
    }

    // Only load once
    if (hasLoaded) return;
    
    const userId = getCurrentUserId();
    if (!userId) {
      setError('Unable to get user ID');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await organizationRoleService.getUserOrganizationRoles(userId);
      const data = response.data || {};
      
      // Get all relevant roles from both accountManagers and supportResponsible
      const accountManagers = data.accountManagers || [];
      const supportResponsible = data.supportResponsible || [];
      const allRoles = [...accountManagers, ...supportResponsible];
      
      // If no relevant roles, don't show the component
      if (allRoles.length === 0) {
        setUserOrganizations([]);
        setHasLoaded(true);
        return;
      }
      
      // Get unique organization IDs
      const orgIds = [...new Set(allRoles.map(role => role.organizationId || role.organization?.id).filter(Boolean))];
      
      // Fetch organization details for each unique organization
      const organizationPromises = orgIds.map(async (orgId) => {
        try {
          const response = await axios.get(`${API_BASE_URL}/organizations/${orgId}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
          });
          return response.data;
        } catch (error) {
          console.error(`Error fetching organization ${orgId}:`, error);
          return { id: orgId, name: `Organization ${orgId.substring(0, 8)}` };
        }
      });
      
      const organizations = await Promise.all(organizationPromises);
      
      // Group by organization and collect unique organizations
      const orgMap = new Map();
      allRoles.forEach(role => {
        const orgId = role.organizationId || role.organization?.id;
        if (orgId) {
          if (!orgMap.has(orgId)) {
            // Find the organization details from our fetched data
            const orgData = organizations.find(org => org.id === orgId) || { id: orgId, name: `Organization ${orgId.substring(0, 8)}` };
            orgMap.set(orgId, {
              ...orgData,
              roles: []
            });
          }
          if (orgMap.has(orgId)) {
            orgMap.get(orgId).roles.push(role.roleType);
          }
        }
      });
      
      const finalOrganizations = Array.from(orgMap.values());
      setUserOrganizations(finalOrganizations);
      setHasLoaded(true);
      
    } catch (err) {
      console.error('Error loading user organizations:', err);
      setError('Failed to load your assigned organizations');
    } finally {
      setLoading(false);
    }
  }, [hasLoaded, isAdmin]);

  useEffect(() => {
    loadUserOrganizations();
  }, [loadUserOrganizations]);

  if (!isAdmin) {
    return null;
  }

  const handleExpandOrg = (organizationId, isExpanded) => {
    setExpandedOrg(isExpanded ? organizationId : null);
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (hasLoaded && userOrganizations.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        You are not assigned as an account manager or support responsible for any organizations.
      </Alert>
    );
  }

  // Don't render anything if still loading or not loaded yet
  if (!hasLoaded) {
    return null;
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Your Assigned Organizations Performance
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Performance overview for organizations where you have management roles (1 week period)
      </Typography>
      
      {userOrganizations.map(organization => (
        <SharedPerformanceCacheProvider key={organization.id}>
          <OrganizationPerformanceCard
            organization={organization}
            onExpand={handleExpandOrg}
          />
        </SharedPerformanceCacheProvider>
      ))}
    </Box>
  );
};

export default PerformanceDashboard;
